import React, { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '../../utils/cn'

interface PasswordInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
 label?: string
 error?: string
 helperText?: string
 leftIcon?: React.ReactNode
 required?: boolean
}

export const PasswordInput: React.FC<PasswordInputProps> = ({
 label,
 error,
 helperText,
 leftIcon,
 className,
 id,
 value,
 required,
 ...props
}) => {
 const [showPassword, setShowPassword] = useState(false)
 const inputId = id || `password-input-${Math.random().toString(36).substr(2, 9)}`
 
 // Garante que value sempre seja uma string para evitar warnings de controlled/uncontrolled
 const safeValue = value === undefined || value === null ? '' : value
 
 const togglePasswordVisibility = () => {
 setShowPassword(!showPassword)
 }

 return (
 <div className="space-y-1">
 {label && (
   <div>
     <label
       htmlFor={inputId}
       className="text-sm font-medium text-foreground"
     >
       {label}
     </label>
     {required && <span className="ml-1 text-sm text-destructive" aria-hidden="true">*</span>}
   </div>
 )}

 <div className="relative">
 {leftIcon && (
 <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
 <div className="h-5 w-5 text-muted-foreground">
 {leftIcon}
 </div>
 </div>
 )}
 
 <input
 id={inputId}
 type={showPassword ? 'text' : 'password'}
 value={safeValue}
 className={cn(
 'block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent bg-card text-foreground transition-colors duration-200',
 error
 ? 'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500'
 : 'border-border',
 leftIcon && 'pl-10',
 'pr-10', // Sempre tem padding à direita para o botão de toggle
 'disabled:bg-surface dark:disabled:bg-gray-900 disabled:text-muted-foreground dark:disabled:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60',
 className
 )}
 {...props}
 />
 
 {/* Botão de toggle de visibilidade */}
 <button
 type="button"
 onClick={togglePasswordVisibility}
 className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer hover:text-muted-foreground dark:hover:text-gray-300 transition-colors"
 aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
 tabIndex={-1}
 >
 {showPassword ? (
 <EyeOff className="h-5 w-5 text-muted-foreground hover:text-muted-foreground dark:hover:text-gray-300" />
 ) : (
 <Eye className="h-5 w-5 text-muted-foreground hover:text-muted-foreground dark:hover:text-gray-300" />
 )}
 </button>
 </div>
 
 {error && (
 <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
 )}
 
 {helperText && !error && (
 <p className="text-sm text-muted-foreground">{helperText}</p>
 )}
 </div>
 )
}

